<script setup lang="ts">
import { computed } from 'vue'
import {
  UFS_VALIDAS,
  TP_PROD_VALIDOS,
  ATIVO_TIPOS_VALIDOS,
  ATIVO_TIPOCONSUMO_VALIDOS,
  CAMPOS_NUMERICOS,
  CAMPOS_SOMENTE_DIGITOS
} from '@/config/validation'

interface Props {
  campo: string
  modelValue: string
  hint?: string
  error?: boolean
  // Modo compacto para uso em célula de tabela (sem label/mensagens)
  denso?: boolean
}

const props = defineProps<Props>()
const emit = defineEmits<{ (e: 'update:modelValue', valor: string): void }>()

// Campos com domínio fixo → dropdown (evita redigitar valor inválido).
// Domínios dinâmicos (mod_trib, ativo_tipoveiculo/tipocacamba, codcat) vêm da
// API/banco e ficam como texto.
const OPCOES_CAMPO: Record<string, string[]> = {
  uf: Array.from(UFS_VALIDAS).sort(),
  tp_prod: TP_PROD_VALIDOS,
  subcod: ['S', 'F'],
  diverso: ['TRUE', 'FALSE'],
  ativo_veicprop: ['TRUE', 'FALSE'],
  ativo_tipo: ATIVO_TIPOS_VALIDOS,
  ativo_tipoconsumo: ATIVO_TIPOCONSUMO_VALIDOS
}

const opcoes = computed<string[] | undefined>(() => OPCOES_CAMPO[props.campo])
// Numéricos reais → input numérico (não aceitam negativos). CPF/CNPJ, CEP, telefones
// e códigos seguem como texto para preservar zeros à esquerda.
const numerico = computed(() => CAMPOS_NUMERICOS.has(props.campo))
// Texto que só aceita dígitos (ex.: CEP) — mantém zeros à esquerda, mas sem letras/símbolos
const somenteDigitos = computed(() => CAMPOS_SOMENTE_DIGITOS.has(props.campo))

const valor = computed({
  get: () => props.modelValue ?? '',
  set: (v: string | null) => {
    let novo = String(v ?? '')
    // Campos numéricos não aceitam valores negativos (valor, peso, capacidade, ano)
    if (numerico.value) novo = novo.replace(/-/g, '')
    // Campos só-dígitos removem qualquer caractere não numérico (cobre também colar)
    if (somenteDigitos.value) novo = novo.replace(/\D/g, '')
    emit('update:modelValue', novo)
  }
})

// Bloqueia sinal/expoente em campos numéricos e letras/símbolos em campos só-dígitos
// (colar é tratado no setter acima)
const bloquearTeclasInvalidas = (e: KeyboardEvent) => {
  if (e.ctrlKey || e.metaKey || e.altKey) return
  if (numerico.value && ['-', '+', 'e', 'E'].includes(e.key)) {
    e.preventDefault()
    return
  }
  if (somenteDigitos.value && e.key.length === 1 && !/[0-9]/.test(e.key)) {
    e.preventDefault()
  }
}
</script>

<template>
  <v-select
    v-if="opcoes"
    v-model="valor"
    :items="opcoes || []"
    :label="denso ? undefined : campo"
    :hint="denso ? undefined : hint"
    :persistent-hint="!denso && !!hint"
    :hide-details="denso ? true : 'auto'"
    :error="error"
    :title="denso ? hint : undefined"
    density="compact"
    variant="outlined"
    clearable
  />
  <v-text-field
    v-else
    v-model="valor"
    :label="denso ? undefined : campo"
    :type="numerico ? 'number' : 'text'"
    :min="numerico ? 0 : undefined"
    :hint="denso ? undefined : hint"
    :persistent-hint="!denso && !!hint"
    :hide-details="denso ? true : 'auto'"
    :error="error"
    :title="denso ? hint : undefined"
    density="compact"
    variant="outlined"
    @keydown="bloquearTeclasInvalidas"
  />
</template>
